<template>
    <main-layout>
        <template #header>
            <main-header :title="'공지사항' | translate">
            </main-header>
        </template>

        <template #toolbar>
            <main-toolbar>
                <div class="notice-toolbar-left">
                    <div class="notice-filter-select notice-filter-select--country">
                        <search-multi-select-box
                                :items="countryOptions"
                                :label="'국가 선택' | translate"
                                @update:selected="onFilterChanged"
                                v-model="searchOptions.countryCodes"
                        />
                    </div>
                    <div class="notice-filter-select notice-filter-select--sortcd">
                        <search-multi-select-box
                                :items="popOptions"
                                :label="'구분 선택' | translate"
                                @update:selected="onFilterChanged"
                                v-model="searchOptions.sortCds"
                        />
                    </div>
                    <div class="notice-filter-select notice-filter-select--pop">
                        <search-single-select-box
                                :items="sortOptions"
                                :label="'게시 여부' | translate"
                                @update:selected="onFilterChanged"
                                v-model="searchOptions.pop"
                        />
                    </div>
                    <div class="notice-filter-select notice-filter-select--date">
                        <search-date-range-picker
                                :label-for-end="'등록기간 종료일' | translate"
                                :label-for-start="'등록기간 시작일' | translate"
                                @update:range="saveSearchDate"
                        />
                    </div>
                </div>
                <div class="notice-toolbar-right">
                    <search-bar
                            :placeholder="'검색어를 입력하세요.' | translate"
                            @update:keyword="onFilterChanged"
                            v-model="searchOptions.searchKeyword"
                    />
                </div>
            </main-toolbar>
        </template>

        <template #body>
            <main-body>
                <vue-data-table
                        ref="dataTable"
                        :fetch="fetch"
                        :headers="headers"
                        item-key="raw.noticeId"
                        @click:row="onClickRow"
                        infinite-pagination
                        :items-per-page="30"
                >
                </vue-data-table>
            </main-body>
        </template>
    </main-layout>
</template>

<script>
    import {PATH} from "@/constants";
    import MainBody from "@/components/common/layout/MainBody";
    import MainToolbar from "@/components/common/layout/MainToolbar";
    import MainHeader from "@/components/common/layout/MainHeader";
    import MainLayout from "@/components/common/layout/MainLayout";
    import SearchDateRangePicker from "@/components/input/SearchDateRangePicker";
    import SearchBar from "@/components/input/SearchBar";
    import SearchMultiSelectBox from "@/components/input/SearchMultiSelectBox";
    import SearchSingleSelectBox from "@/components/input/SearchSingleSelectBox";
    import TotalCodeOptions from "@/assets/options/TotalCodeOptions";
    import Notice from "@/model/notice";
    import {dateHelper} from "@/assets/plugins/mps-common/time/date-helper";
    import VueDataTable from "@/assets/plugins/vue-data-table/VueDataTable";

    export default {
        name: "NoticeList",
        components: {
            VueDataTable,
            SearchSingleSelectBox,
            SearchMultiSelectBox,
            SearchBar,
            SearchDateRangePicker,
            MainLayout,
            MainHeader,
            MainToolbar,
            MainBody
        },
        mixins: [TotalCodeOptions],
        data() {
            return {
                headers: [
                    {
                        text: "제목",
                        value: "raw.subject",
                        width: 200,
                    },
                    {
                        text: "내용",
                        value: "raw.content",
                        width: 700,
                    },
                    {
                        text: "태그",
                        value: "raw.tags",
                        width: 240,
                    },
                    {
                        text: "국가",
                        value: "raw.countryCode",
                        width: 200,
                    },
                    {
                        text: "구분",
                        value: "raw.sortCdName",
                        width: 100,
                    },
                    {
                        text: "팝업공지",
                        value: "pop-notice",
                        width: 400,
                        child: [
                            {
                                text: "게시 여부",
                                value: "raw.pop",
                            },
                            {
                                text: "게시 시작일",
                                value: "raw.popupStartDate",
                                read: (v, item) => dateHelper.formatDate(v),
                            },
                            {
                                text: "게시 종료일",
                                value: "raw.popupEndDate",
                                read: (v, item) => dateHelper.formatDate(v),
                            },
                        ]
                    },
                    {
                        text: "작성자",
                        value: "raw.register",
                        width: 200,
                    },
                    {
                        text: "등록일자",
                        value: "raw.regDatetime",
                        read: (v, item) => dateHelper.formatDate(v),
                        width: 200,
                    },
                ],
                searchOptions: {
                    searchKeyword: null,
                    startDate: null,
                    endDate: null,
                    countryCodes: [],
                    sortCds: [],
                    pop: null,
                },
            }
        },
        created() {
            this.loadCountryOptions();
            this.loadnoticePopOptions();
            this.loadNoticeSortOptions();
        },
        methods: {
            async fetch(pageNumber, pageSize) {
                const res = await this.$request(PATH.NOTICE.LIST)
                    .setObject({
                        ...this.searchOptions,
                        pageNumber: pageNumber,
                        pageSize: pageSize,
                    })
                    .catch()
                    .enqueue();
                return res.data.map(e => new Notice(e))
            },

            /**
             * 검색기간이 변경되었습니다.
             */
            saveSearchDate(obj) {
                this.searchOptions.startDate = obj.start;
                this.searchOptions.endDate = obj.end;

                this.onFilterChanged();
            },

            /**
             * 필터 조건이 변경되었습니다. 목록을 초기화하고 다시 불러옵니다.
             */
            onFilterChanged() {
                this.$refs.dataTable.clearAndNext();
            },

            onClickRow(item) {
                this.$router.push({name: "managertnbox.notice.view", params: { id : item.raw.noticeId }})
            },
        }
    }
</script>

<style scoped>
.notice-toolbar-left {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.notice-toolbar-right {
    margin-left: auto;
    width: 450px;
    min-width: 320px;
}

.notice-filter-select {
    flex: 0 0 auto;
}

.notice-filter-select--country { width: 125px; }
.notice-filter-select--sortcd { width: 180px; }
.notice-filter-select--pop { width: 160px; }
.notice-filter-select--date { width: 320px; }

@media (max-width: 1500px) {
    .notice-toolbar-right {
        width: 360px;
        min-width: 260px;
    }
}
</style>
